import type { AddressDTO } from 'gw-edge-client/dist/types/dto/pc/edge/capabilities/address/dto';
import type { FieldEventDataProps } from '@flos/react-ui/build/core/forms/address-field/address-field';
import type {
    MissingInformationDTO,
    SingleTripMissingInformationDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/missingInformation';
import { Grid, GridItem, TextField, AddressField, CprField } from '@flos/react-ui';
import React from 'react';
import { type SetterOrUpdater, useSetRecoilState } from 'recoil';
import { missingInformationAtom } from '../../../recoil/atoms/missingInformationAtom';

const SingleTripMissingInformationForm = ({
    singleTripMissingDTO,
    isMultiplePersons,
    isSTT,
    personIndex
}: {
    singleTripMissingDTO: SingleTripMissingInformationDTO;
    isMultiplePersons: boolean;
    isSTT: boolean;
    personIndex: string;
}): React.JSX.Element => {
    const setMissingInformation = useSetRecoilState(missingInformationAtom);
    const flavourMissingName = isSTT ? 'singleTripTravel' : 'singleTripCancellation';

    const { fixedId, name, surname, cpr, address } = singleTripMissingDTO;
    if (!fixedId) {
        return <></>;
    }

    const getPersonDescription = (fixedId: string): string => {
        return `${
            isMultiplePersons ? 'Medforsikrede' : 'Medforsikret'
        } ${personIndex}, som ikke bor på din adresse`;
    };

    const setAddress = (fieldEventDataProps: FieldEventDataProps): void => {
        const { address, addressInfo } = fieldEventDataProps;
        const addressData: AddressDTO = {
            displayName: address,
            addressLine1: addressInfo.streetName,
            addressLine2: addressInfo.houseNumber,
            addressLine3:
                addressInfo.floor !== '' && addressInfo.door !== ''
                    ? `${String(addressInfo.floor)}, ${String(addressInfo.door)}`
                    : '',
            city: addressInfo.postalCodeName,
            postalCode: addressInfo.zipCode,
            country: 'DK'
        };
        updateMissingInformationPropertySingleTrip({ address: addressData });
    };

    const updateMissingInformationPropertySingleTrip = (
        updateValue: SingleTripMissingInformationDTO
    ): void => {
        updateMissingInformationProperty(fixedId, updateValue, setMissingInformation);
    };

    const updateMissingInformationProperty = (
        fixedId: string,
        updateValue: SingleTripMissingInformationDTO,
        setMissingInformation: SetterOrUpdater<MissingInformationDTO>
    ): void => {
        setMissingInformation((previousState) => {
            const updateDto = previousState[flavourMissingName]?.map(
                (item: SingleTripMissingInformationDTO) => {
                    if (item.fixedId !== fixedId) return item;
                    return {
                        ...item,
                        ...updateValue
                    };
                }
            );
            return {
                ...previousState,
                [flavourMissingName]: updateDto
            };
        });
    };

    return (
        <>
            <React.Fragment key={fixedId}>
                <Grid gap={'none'}>
                    <GridItem span={12} className="u-spacing-stack-s">
                        <h6 className="u-spacing-stack-none">{getPersonDescription(fixedId)}</h6>
                    </GridItem>
                    <GridItem span={12}>
                        <Grid gap={3}>
                            <GridItem span={6}>
                                <TextField
                                    label={'Fornavn'}
                                    id={`${flavourMissingName}-missing-name-${fixedId}`}
                                    defaultValue={name}
                                    pattern="[ a-zA-Z\u00C0-\u017F'-]+"
                                    minLength={1}
                                    maxLength={32}
                                    onBlur={(data) => {
                                        updateMissingInformationPropertySingleTrip({
                                            name: data.currentTarget.value
                                        });
                                    }}
                                    showLength={false}
                                />
                            </GridItem>
                            <GridItem span={6}>
                                <TextField
                                    label={'Efternavn'}
                                    id={`${flavourMissingName}-missing-surname-${fixedId}`}
                                    defaultValue={surname}
                                    pattern="[a-zA-Z\u00C0-\u017F'-]+"
                                    minLength={1}
                                    maxLength={32}
                                    onBlur={(data) => {
                                        updateMissingInformationPropertySingleTrip({
                                            surname: data.currentTarget.value
                                        });
                                    }}
                                    showLength={false}
                                />
                            </GridItem>
                        </Grid>
                    </GridItem>
                    <GridItem span={12}>
                        <AddressField
                            label={'Adresse'}
                            id={`${flavourMissingName}-missing-address-${fixedId}`}
                            defaultValue={address?.displayName}
                            fieldEvent={(data) => {
                                setAddress(data);
                            }}
                        />
                    </GridItem>
                    <GridItem span={12}>
                        <CprField
                            label={'CPR-nummer'}
                            id={`${flavourMissingName}-missing-cpr-${fixedId}`}
                            value={cpr}
                            onBlur={(data) => {
                                updateMissingInformationPropertySingleTrip({
                                    cpr: data.target.value.replace(/\D/g, '')
                                });
                            }}
                        />
                    </GridItem>
                </Grid>
            </React.Fragment>
        </>
    );
};

export default SingleTripMissingInformationForm;
